import { Link } from "react-router-dom";
import PreOfferBanner from "@/components/PreOfferBanner";

const ROWS = [
  {
    title: "What I want to do",
    paragraphs: [
      "I want to help traders who have blown up repeatedly and still cannot explain why. They should not have to conclude that they are broken.",
      "I want them to have an accurate name for what happens under pressure, and something structural to build around instead of another promise to try harder.",
      "A trader can possess a working edge and still become unable to execute it when size, urgency, identity or pressure changes the operator. Making that problem visible is where the work begins.",
    ],
  },
  {
    title: "Why the book is free",
    paragraphs: [
      "THE CAGE is the complete argument, the mechanism, the evidence, the philosophy, the breakthroughs from 168 journal entries and nine years of trading.",
      "I would rather ten thousand traders read it than three hundred buy it.",
      "A diagnosis behind a paywall helps almost no one. A diagnosis in circulation might change how traders understand and talk about their worst days.",
      "That is why the book is free.",
    ],
  },
  {
    title: "What comes after understanding",
    paragraphs: [
      "The book gives you the complete argument and enough architecture to build your own version. Building it required nine years of trading, real capital, repeated failures and 168 journal entries.",
      <>
        <Link to="/cage-system" className="underline decoration-[#69d2c7] underline-offset-4 hover:decoration-ink transition-colors" data-testid="the-mission-cage-system-link">The Cage System</Link> is the operating version I built from that work: the permissions, limits, decision pages and session structure already connected and tested in my own trading.
      </>,
      "The system already runs in my trading. The work before 24 October is turning it into something another trader can run without me beside them.",
    ],
  },
  {
    title: "What I owe in return",
    paragraphs: [
      "Anyone can publish a framework and overclaim what it proves. I do not want to build this community around selected P&L screenshots, broker-statement flexing or carefully chosen winning days. Judge the work instead by its depth, and by a public record that includes what goes wrong.",
      "Beginning 15 September 2026, the same rules will be tested through regular public live sessions, including red days, violations and legitimate no-trade sessions. If the structure fails under pressure, that will be visible too.",
      "That is what I owe in return for asking anyone to trust the work: a record I do not get to curate.",
    ],
  },
];

export default function TheMission() {
  return (
    <div data-testid="page-the-mission">
      {/* HERO */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-16 md:pb-24">
          <div className="label-mono text-muted" data-testid="the-mission-eyebrow">The Mission</div>
          <h1 className="font-display mt-8 text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.04] tracking-tight max-w-5xl" data-testid="the-mission-headline">
            I want to help traders who are stuck where I was stuck.
          </h1>
          <h2 className="font-display-medium mt-6 text-2xl sm:text-3xl md:text-4xl max-w-4xl" data-testid="the-mission-subheadline">
            That is the whole mission. Everything I build, from the free book to the tools and support that follow, serves it.
          </h2>
        </div>
      </section>

      {/* ROWS */}
      {ROWS.map((row) => (
        <section key={row.title} className="border-b border-hairline">
          <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <h3 className="md:col-span-4 section-label" data-testid={`the-mission-row-${row.title.toLowerCase().replace(/\s+/g, "-")}`}>
              {row.title}
            </h3>
            <div className="md:col-span-7 md:col-start-6 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.85] space-y-6 max-w-2xl">
              {row.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* FINAL CTA */}
      <section>
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <h2 className="font-display text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.05] tracking-tight" data-testid="the-mission-cta-headline">
            Start with THE CAGE.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-muted leading-[1.5] md:leading-[1.85] max-w-2xl">
            The complete argument is free. Read it before deciding whether anything else is useful to you.
          </p>
          <Link
            to="/ebook"
            className="mt-10 inline-flex items-center gap-3 bg-ink text-white px-8 py-4 label-mono hover:bg-leaf transition-colors"
            data-testid="the-mission-cta-link"
          >
            Read THE CAGE →
          </Link>
        </div>
      </section>
      <PreOfferBanner ctaHref="/ebook#cage-sale" ctaLabel="Get the free book" />
    </div>
  );
}
